<template>
    <div
        :id="id"
        class="WSelect"
        :class="{ error }"
    >
        <Multiselect
            ref="multiSelect"
            v-model="selectedValue"
            :searchable="searchable || multiple"
            :options="selectOptions"
            :multiple="multiple"
            :placeholder="placeholder"
            :closeOnSelect="closeOnSelect"
            :optionHeight="30"
            :loading="loading"
            :disabled="disabled"
            :showLabels="showLabels"
            :preselectFirst="preselectFirst"
            :internalSearch="internalSearch"
            trackBy="key"
            label="label"
            :groupValues="selectAll ? 'options' : undefined"
            :groupLabel="selectAll ? 'title' : undefined"
            :groupSelect="selectAll ? true : undefined"
            v-on="inputListeners"
        >
            <template
                #selection="{ values, search, isOpen }"
            >
                <span
                    v-if="values.length &amp;&amp; !isOpen"
                    class="multiselect__single"
                >
                    <template
                        v-for="(item, index) in values"
                    >
                        {{ item.label }}
                        <template v-if="index !== values.length - 1">
                            ,
                        </template>
                    </template>
                </span>
            </template>
            <template #noResult>
                <span>
                    {{ $t('error.select.noOptions') }}
                </span>
            </template>
        </Multiselect>
        <Transition
            name="fade"
            :duration="500"
        >
            <WText
                v-if="error"
                class="Error"
                typo="span"
                :size="12"
                color="red"
            >
                {{ error }}
            </WText>
        </Transition>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import Multiselect from 'vue-multiselect'
import { noop, merge, debounce } from 'lodash'
import { generateIdMixin } from '@/mixins'

export default {
    name: 'WSelect',
    components: {
        Multiselect,
    },
    mixins: [generateIdMixin],
    props: {
        name: VueTypes.string.isRequired,
        options: VueTypes.array.isRequired,
        placeholder: VueTypes.string.def(''),
        value: VueTypes.oneOfType([Object, String, Array, Number, null]).isRequired,
        error: VueTypes.string.def(''),
        multiple: VueTypes.bool.def(false),
        disabled: VueTypes.bool.def(false),
        loading: VueTypes.bool.def(false),
        selectAll: VueTypes.bool.def(false),
        showLabels: VueTypes.bool.def(false),
        preselectFirst: VueTypes.bool.def(false),
        internalSearch: VueTypes.bool,
        searchable: VueTypes.bool.def(false),
        closeOnSelect: VueTypes.bool.def(true),
        debounceWait: VueTypes.number.def(1000),
    },
    computed: {
        inputListeners () {
            return merge({}, this.$listeners, {
                input: noop,
                'search-change': debounce((value) => {
                    this.$emit('search', value)
                }, this.debounceWait),
            })
        },
        selectOptions () {
            return !this.selectAll ? this.options : [{
                title: this.$t('actions.selectAll'),
                options: this.options,
            }]
        },
        selectedValue: {
            get () {
                if (Array.isArray(this.value)) return this.options.filter(o => this.value.includes(o.key))
                return this.options.find(o => o.key === this.value)
            },
            set (value) {
                if (value) {
                    const newValue = Array.isArray(value) ? value.map(el => el.key) : value.key
                    this.$emit('input', newValue, this.id)
                } else {
                    this.$emit('input', '', this.id)
                }
            },
        },
    },
    beforeDestroy () {
        if (this.$refs.multiSelect) {
            this.$refs.multiSelect.deactivate()
        }
    },
}
</script>
<style lang="scss">
@import '~vue-multiselect/dist/vue-multiselect.min.css';
@import '@/theme/mixins/_global.scss';

.WSelect {
    position: relative;
    .multiselect {
        min-height: $form-height;
        border-bottom: $form-border;
        border: $form-border;
        border-radius: $border-radius;
        color: $form-color;
        font-size: $form-font-size;
        font-weight: $form-font-weight;
        box-sizing: border-box;
        transition: border-color .5s ease-in-out;

        &__tags {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            min-height: $form-height - 2;
            margin-right: 40px;
            padding: 0px 0px 0 $form-padding;
            border: none;
            border-radius: 0;
            background: transparent;

            &-wrap {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                width: 100%;
                margin-top: 5px;
            }
        }
        &__tag {
            background: rgba($accent-blue, 0.6);

            &-icon {
                color: $white !important;

                &:hover {
                    background: darken($accent-blue, .5)
                }
            }
        }
        &__single {
            margin-bottom: 0;
            padding-left: 0;
            border-radius: 0;
            background: transparent;
            font-size: $form-font-size;
            line-height: 26px;
            vertical-align: middle;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
        &__input {
            left: -#{$form-padding};
            min-height: $form-height - 2;
            margin-bottom: 0;
            border-radius: 0;
            background: transparent;
            color: $form-color;
            font-size: $form-font-size;
            @include placeholder {
                color: $form-placeholder-color !important;
            }
        }
        &__placeholder {
            margin-bottom: 0;
            padding-top: 0;
            color: $form-placeholder-color;
        }
        &__select {
            top: 0;
            height: $form-height - 2;
            padding: 4px 8px;
            &:before {
                display: inline-block;
                top: 0;
                width: 8px;
                height: 8px;
                margin-top: #{$form-height / 2 - 10px};
                border-color: $form-placeholder-color;
                border-style: solid;
                border-width: 1px 1px 0 0;
                box-sizing: inherit;
                vertical-align: text-top;
                transform: rotate(133deg);
            }
        }
        &__content-wrapper {
            padding: $form-padding 0;
            border: none;
            border-bottom-left-radius: $border-radius;
            border-bottom-right-radius: $border-radius;
            border-top: $form-border;
            background-color: $form-dropdown-bg;
            box-shadow: 0 3px 6px -3px rgba(0, 0, 0, 0.2);
            z-index: 5;
        }
        &__option {
            min-height: 30px;
            padding: 7px;
            &:after {
                line-height: 30px;
            }
        }
        &__option--group {
            background: transparent;
            color: $form-color;
        }
        &__option--group-selected {
            background: rgba($accent-blue, 0.25);
            color: $form-color;
        }
        &__option--selected {
            background: rgba($accent-blue, 0.25);
            color: $form-color;
            font-weight: $font-weight-base;
        }
        &__option--highlight {
            background: rgba($accent-blue, 0.6) !important;
            color: $white;
            &:after {
                background: transparent;
                color: $form-color;
            }
        }
        &__spinner {
            width: 40px;
            height: 32px;

            &:before, &:after {
                margin: -10px 0 0 -10px;
                border-top-color: $accent-blue;
            }
        }
        &--disabled, &--disabled .multiselect__current, &--disabled .multiselect__select {
            background-color: $form-disabled-background;
            opacity: 1;
            cursor: not-allowed;
        }
    }
    &.error {
        .multiselect,
        .multiselect__content-wrapper {
            border-color: $danger;
        }
    }
}

.Error {
    position: absolute;
    top: $form-height + 5px;
    left: 5px;
}
</style>
